import { ArrowRight, Phone } from "lucide-react";
import { Breadcrumbs, PageHero } from "@/components/site/PageHero";
import { Button } from "@/components/ui/button";
import { business } from "@/config/business";
import { blogPosts, getBlogPost, type BlogPost } from "@/config/blog-posts";
import type { PageUi } from "@/i18n/content";
import { trackAnalyticsEvent } from "@/lib/analytics";

type PathBuilder = (href: string) => string;

export function BlogIndexPage({ path, ui }: { path: PathBuilder; ui?: PageUi | undefined }) {
  return (
    <>
      <PageHero
        eyebrow="Roofing guides"
        title="Practical roofing notes for Metro Vancouver homeowners"
        body="Educational articles on assessment, repair, and system choice from Revolin Roofing, a Revolin Group company serving the Lower Mainland."
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: ui?.common.home ?? "Home", href: path("/") },
              { label: "Blog" },
            ]}
          />
        }
      />
      <section className="section-y">
        <div className="container-page grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {blogPosts.map((post) => (
            <article
              key={post.slug}
              className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm"
            >
              <img
                src={post.heroImage}
                alt={post.heroAlt}
                width={1200}
                height={675}
                loading="lazy"
                className="aspect-[16/9] w-full object-cover"
              />
              <div className="flex flex-1 flex-col p-6">
                <p className="text-xs text-muted-foreground">
                  {post.publishedAt} · {post.readingTime}
                </p>
                <h2 className="mt-2 text-xl font-semibold leading-snug">{post.title}</h2>
                <p className="mt-3 flex-1 text-sm leading-7 text-muted-foreground">{post.excerpt}</p>
                <a
                  href={path(`/blog/${post.slug}`)}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-accent"
                >
                  Read guide <ArrowRight className="size-4" aria-hidden="true" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

export function BlogPostPage({
  post,
  path,
  ui,
}: {
  post: BlogPost;
  path: PathBuilder;
  ui?: PageUi | undefined;
}) {
  return (
    <>
      <PageHero
        eyebrow="Roofing guide"
        title={post.title}
        body={post.excerpt}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: ui?.common.home ?? "Home", href: path("/") },
              { label: "Blog", href: path("/blog") },
              { label: post.title },
            ]}
          />
        }
      />
      <article className="section-y border-b border-border">
        <div className="container-page grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.42fr)]">
          <div className="max-w-3xl space-y-10">
            <img
              src={post.heroImage}
              alt={post.heroAlt}
              width={1400}
              height={788}
              className="w-full rounded-xl border border-border object-cover"
            />
            {post.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="display-3 text-balance">{section.heading}</h2>
                <div className="mt-4 space-y-4 text-sm leading-7 text-muted-foreground">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
            {post.detailImage ? (
              <img
                src={post.detailImage}
                alt={post.detailAlt ?? ""}
                width={1200}
                height={800}
                loading="lazy"
                className="w-full rounded-xl border border-border object-cover"
              />
            ) : null}
            {post.faqs.length ? (
              <section>
                <h2 className="display-3">Frequently asked questions</h2>
                <dl className="mt-6 space-y-6">
                  {post.faqs.map((faq) => (
                    <div key={faq.question}>
                      <dt className="font-semibold text-foreground">{faq.question}</dt>
                      <dd className="mt-2 text-sm leading-7 text-muted-foreground">{faq.answer}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ) : null}
          </div>
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-xl border border-border bg-muted/40 p-6">
              <p className="eyebrow">Related services</p>
              <ul className="mt-4 space-y-3 text-sm">
                {post.relatedServicePaths.map((href) => (
                  <li key={href}>
                    <a href={path(href)} className="font-medium underline-offset-4 hover:underline">
                      {href.replace(/^\//, "").replace(/\//g, " · ")}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <p className="text-sm leading-7 text-muted-foreground">
                Revolin Roofing serves Metro Vancouver and the Lower Mainland. Request a roof assessment when you are ready to confirm conditions on your property.
              </p>
              <div className="mt-5 flex flex-col gap-3">
                <Button variant="accent" asChild>
                  <a
                    href={path("/roof-estimate")}
                    onClick={() =>
                      trackAnalyticsEvent({
                        event: "estimate_cta_click",
                        cta_location: "blog_sidebar",
                        lead_type: "planned",
                      })
                    }
                  >
                    Request assessment
                  </a>
                </Button>
                <Button variant="outline" asChild>
                  <a
                    href={business.phoneHref}
                    onClick={() =>
                      trackAnalyticsEvent({ event: "phone_click", link_location: "blog_sidebar" })
                    }
                  >
                    <Phone className="size-4" aria-hidden="true" /> {business.phone}
                  </a>
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </article>
    </>
  );
}

export { getBlogPost };
